import { useState } from 'react';

import { AddPaymentMethodDialog } from '@/components/billing/payment-methods/add-payment-method-dialog';
import UpdatePaymentMethodDialog from '@/components/billing/payment-methods/update-payment-method-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { MutedText } from '@/components/ui/typography';
import {
  getPaymentMethodIcon,
  PaymentMethodIcon,
  paymentMethodLabel,
} from '@/lib/billing/payment-method-icons';
import type { PaymentMethod } from '@/pages/cms/billing/types';

// Single-line option, unlike PaymentMethodRow's two-line layout, so Link's email goes inline.
function selectorLabel(paymentMethod: PaymentMethod, brandLabel: string): string {
  if (paymentMethod.type === 'link') return `Link (${paymentMethod.email})`;
  return paymentMethodLabel(paymentMethod, brandLabel);
}

interface PaymentMethodSelectorProps {
  paymentMethods: PaymentMethod[];
  selectedId: string;
  onSelect: (paymentMethodId: string) => void;
  stripePublicKey: string;
  googleMapsApiKey?: string;
  canAddPaymentMethod: boolean;
  onAdded?: (paymentMethodId: string) => void;
  onUpdated?: () => void;
  idPrefix: string;
  /** Payment method types shown in the list but not selectable, e.g. types this flow can't charge. */
  disabledTypes?: PaymentMethod['type'][];
}

function PaymentMethodOption({
  paymentMethod,
  stripePublicKey,
  googleMapsApiKey,
  onUpdated,
  idPrefix,
  disabled,
}: {
  paymentMethod: PaymentMethod;
  stripePublicKey: string;
  googleMapsApiKey?: string;
  onUpdated?: () => void;
  idPrefix: string;
  disabled?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const { label: brandLabel } = getPaymentMethodIcon(paymentMethod);
  const optionId = `${idPrefix}-${paymentMethod.id}`;

  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex min-w-0 items-center gap-2">
        <RadioGroupItem
          id={optionId}
          value={paymentMethod.id}
          className="size-5"
          disabled={disabled}
        />
        <PaymentMethodIcon paymentMethod={paymentMethod} />
        <Label
          htmlFor={optionId}
          className={
            disabled
              ? 'text-sm leading-5 font-normal text-muted-foreground'
              : 'cursor-pointer text-sm leading-5 font-normal'
          }
        >
          {selectorLabel(paymentMethod, brandLabel)}
        </Label>
        {paymentMethod.default ? (
          <Badge size="sm" className="rounded-md px-1.5 py-0.5 font-medium">
            Default
          </Badge>
        ) : null}
      </div>

      {paymentMethod.type === 'card' ? (
        <>
          <Button
            variant="link"
            className="h-auto shrink-0 p-0"
            aria-label={`Edit ${brandLabel} ending in ${paymentMethod.last4}`}
            onClick={() => setEditing(true)}
          >
            Edit
          </Button>
          <UpdatePaymentMethodDialog
            paymentMethod={paymentMethod}
            stripePublicKey={stripePublicKey}
            googleMapsApiKey={googleMapsApiKey}
            open={editing}
            onOpenChange={setEditing}
            onUpdated={onUpdated}
          />
        </>
      ) : null}
    </div>
  );
}

export function PaymentMethodSelector({
  paymentMethods,
  selectedId,
  onSelect,
  stripePublicKey,
  googleMapsApiKey,
  canAddPaymentMethod,
  onAdded,
  onUpdated,
  idPrefix,
  disabledTypes,
}: PaymentMethodSelectorProps) {
  return (
    <div className="flex flex-col gap-4">
      {paymentMethods.length > 0 ? (
        <RadioGroup
          value={selectedId}
          onValueChange={onSelect}
          className="gap-2"
          aria-label="Payment method"
        >
          {paymentMethods.map((paymentMethod) => (
            <PaymentMethodOption
              key={paymentMethod.id}
              paymentMethod={paymentMethod}
              stripePublicKey={stripePublicKey}
              googleMapsApiKey={googleMapsApiKey}
              onUpdated={onUpdated}
              idPrefix={idPrefix}
              disabled={disabledTypes?.includes(paymentMethod.type)}
            />
          ))}
        </RadioGroup>
      ) : (
        <MutedText>No payment methods available.</MutedText>
      )}
      <AddPaymentMethodDialog
        canAddPaymentMethod={canAddPaymentMethod}
        stripePublicKey={stripePublicKey}
        onAdded={onAdded}
      />
    </div>
  );
}
